import {
  getBoundingBox,
  Point,
  translateBox,
} from "@/features/Editor/Canvas/canvas-geometry";
import type { CanvasElement } from "./canvas-state";
import type {
  Gesture,
  GestureOutput,
  Modifiers,
  PressContext,
} from "./canvas-gesture";
import {
  getGuidelines,
  getSnapOffset,
  getSnapTargets,
} from "./canvas-snapping";

// Pressed on an element or inside the selection box (targetId is null on empty space).
// A drag moves the selection; a click resolves which elements are selected. An already
// selected element stays selected on press so the selection can be dragged as a whole.
export function createMoveGesture(
  context: PressContext,
  targetId: string | null,
): Gesture {
  const { canvasElements, selectedIds, modifiers } = context;
  const wasTargetSelected = targetId !== null && selectedIds.includes(targetId);
  const selectedIdsAfterPress =
    targetId === null || wasTargetSelected
      ? selectedIds
      : modifiers.shift
        ? [...selectedIds, targetId]
        : [targetId];
  const move = createMove(
    context,
    canvasElements.filter(({ externalId }) =>
      selectedIdsAfterPress.includes(externalId),
    ),
  );

  return {
    press: () => ({ selectedIds: selectedIdsAfterPress }),
    drag: move,
    click: (modifiers) => {
      if (!modifiers.shift) {
        return { selectedIds: targetId ? [targetId] : [] };
      }

      return wasTargetSelected
        ? { selectedIds: selectedIds.filter((id) => id !== targetId) }
        : {};
    },
  };
}

function createMove(
  { start, canvasElements, resolution, scale }: PressContext,
  movingElements: CanvasElement[],
) {
  const selectionBox = getBoundingBox(movingElements.map(({ box }) => box));
  const snapTargets = getSnapTargets(
    canvasElements
      .filter((element) => !movingElements.includes(element))
      .map(({ box }) => box),
    resolution,
  );

  return (point: Point, { shift }: Modifiers): GestureOutput => {
    if (!selectionBox) {
      return {};
    }

    let dx = point.x - start.x;
    let dy = point.y - start.y;
    const isXLocked = shift && Math.abs(dx) <= Math.abs(dy);
    const isYLocked = shift && !isXLocked;

    const snap = getSnapOffset(
      translateBox(selectionBox, dx, dy),
      snapTargets,
      scale,
    );

    dx = isXLocked ? 0 : Math.round(dx + snap.dx);
    dy = isYLocked ? 0 : Math.round(dy + snap.dy);

    return {
      drafts: Object.fromEntries(
        movingElements.map(({ externalId, box }) => [
          externalId,
          translateBox(box, dx, dy),
        ]),
      ),
      guidelines: getGuidelines(
        translateBox(selectionBox, dx, dy),
        snapTargets,
      ),
    };
  };
}
